import { UserGuideSection } from './user-guide.model';

export const USER_GUIDE_SECTIONS: ReadonlyArray<UserGuideSection> = [
  {
    id: 'getting-started',
    title: 'Getting Started and Navigation',
    intro: 'Use the top navigation to move between the main areas of the portal and keep an eye on your selected site.',
    tasks: [
      {
        heading: 'Find the main work areas quickly',
        body: 'Dashboard, Properties, New Developments, Calendar, Office, Settings, and Admin are all reached from the main navigation when your role has access.',
        bullets: [
          'Dashboard gives you a summary view of activity and updates.',
          'Properties is where you manage listings and listing changes.',
          'Calendar helps you manage scheduled property activity.',
          'Office and Admin are permission-based and may not appear for every user.',
        ],
      },
      {
        heading: 'Understand site context',
        body: 'Many screens use the currently selected office or group to decide what data you see and what actions are available.',
        bullets: [
          'If something looks missing, first confirm you are in the right site.',
          'Use the profile menu to switch location when you need to work in another office or group.',
        ],
      },
    ],
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    intro: 'The dashboard is designed to surface high-level information without requiring you to open each feature area first.',
    tasks: [
      {
        heading: 'Read the summary cards',
        body: 'Use the dashboard cards and statistics panels to review listing activity, feeds, and key performance indicators for the selected site.',
      },
      {
        heading: 'Use the dashboard as a launch point',
        body: 'Treat the dashboard as a starting page rather than the full workflow.',
        bullets: [
          'Open detailed property work from the Properties area.',
          'Use the calendar actions when you need event-level scheduling details.',
          'Review notices, news, and product information here before moving into task-specific screens.',
        ],
      },
    ],
  },
  {
    id: 'properties-workflows',
    title: 'Properties Workflows',
    intro: 'The Properties area is the core workspace for adding, updating, and reviewing listings.',
    tasks: [
      {
        heading: 'Create and edit listings',
        body: 'Use the property forms to build a new listing or update an existing one, including details, media, pricing, and publishing-related information.',
        bullets: [
          'Complete required fields before attempting to move through the full workflow.',
          'Review validation messages carefully when a step cannot be completed.',
          'Use the summary or review stages to confirm your final listing details before publishing changes.',
        ],
      },
      {
        heading: 'Manage listing changes safely',
        body: 'When editing an active listing, confirm that pricing, status, media, and descriptive content are all still aligned before saving or publishing.',
      },
      {
        heading: 'Know when to ask for help',
        body: 'If a workflow is blocked by business rules, unavailable options, or uncertainty around listing requirements, contact the appropriate internal owner or account support channel.',
      },
    ],
  },
  {
    id: 'calendar-basics',
    title: 'Calendar Basics',
    intro: 'The Calendar helps coordinate property-related events such as viewings and scheduling activity.',
    tasks: [
      {
        heading: 'Open and review scheduled activity',
        body: 'Use the calendar to inspect dates with activity, open event details, and identify what is planned across the selected site.',
      },
      {
        heading: 'Add or update event details',
        body: 'When event creation or editing is available, use the dialog workflows to confirm the event type, property, and timing before saving.',
        bullets: [
          'Double-check the property association before confirming an event.',
          'Use edit or delete actions carefully so the calendar stays accurate for the team.',
        ],
      },
    ],
  },
  {
    id: 'office-basics',
    title: 'Office and User Management Basics',
    intro: 'Some areas of the app are limited by role. If you do not see Office or Admin sections, that is expected for some users.',
    tasks: [
      {
        heading: 'Use Office screens when you have access',
        body: 'Office screens typically cover business details, metrics, users, billing, enquiries, and other operational settings for the selected site.',
        bullets: [
          'Only update office or user information when you are responsible for that data.',
          'Check the selected site before making office-level changes.',
        ],
      },
      {
        heading: 'Use Admin screens cautiously',
        body: 'Admin tools are intended for higher-permission workflows such as account-manager administration, group and office management, or troubleshooting tasks.',
      },
    ],
  },
  {
    id: 'profile-and-location',
    title: 'Switching Location and Profile Basics',
    intro: 'The profile menu in the navbar gives you quick account actions that affect the whole app session.',
    tasks: [
      {
        heading: 'Switch location when changing context',
        body: 'Use the Switch Location action from your profile menu to move to a different office or group before starting work in that context.',
      },
      {
        heading: 'Confirm your signed-in details',
        body: 'The profile menu also shows your current display name, role, and selected site so you can verify you are working under the expected account context.',
      },
      {
        heading: 'Sign out when you finish',
        body: 'Use Sign out from the same menu to close your session, especially on a shared machine or when changing users.',
      },
    ],
  },
  {
    id: 'tips-and-support',
    title: 'Tips, Limitations, and Where to Get Help',
    intro: 'This first version of the guide focuses on common workflows and app orientation rather than documenting every field or edge case.',
    tasks: [
      {
        heading: 'Use this guide as a practical starting point',
        body: 'Return here when you need a reminder of where a workflow lives or which area of the app should be used for a given task.',
      },
      {
        heading: 'Expect role-based differences',
        body: 'Menus, screens, and actions can vary by role and selected site, so you may not see every item described in this guide.',
      },
      {
        heading: 'Escalate unresolved issues',
        body: 'If the guide does not answer your question, collect the page, action, and any validation or error message you encountered before contacting support or your internal owner.',
      },
    ],
  },
];
